import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Button, Stack, TextField, MenuItem, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import { API_BASE_URL } from "../config";
import { REMINDER_INTERVAL_HOURS, REMINDER_MAX_OCCURRENCES } from "../constants";

const REMINDERS_API = `${API_BASE_URL}/reminders`;

const pad = (n) => String(n).padStart(2, "0");
// A local "YYYY-MM-DDTHH:mm" string for a datetime-local input.
const toLocalInput = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const hoursFromNow = (h) => new Date(Date.now() + h * 3600 * 1000);

/**
 * Create or edit a single incident review reminder.
 *
 * Props:
 *  - open, onClose, onSaved
 *  - incident: { id, incident_type, incident_summary, patient_name } for display
 *  - reminder: the existing reminder to edit, or null to create a new one
 */
export default function ReminderFormDialog({ open, onClose, onSaved, incident, reminder, me }) {
  const editing = !!reminder;
  const [recurring, setRecurring] = useState(false);
  const [intervalHours, setIntervalHours] = useState(1);
  const [occurrences, setOccurrences] = useState(2);
  const [nextTime, setNextTime] = useState(toLocalInput(hoursFromNow(1)));
  // Once the user edits the time by hand we stop auto-deriving it from the pattern.
  const [timeTouched, setTimeTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  // (Re)initialise whenever the dialog opens or the reminder being edited changes.
  useEffect(() => {
    if (!open) return;
    if (reminder) {
      setRecurring(!!reminder.recurring);
      setIntervalHours(reminder.interval_hours || 1);
      setOccurrences(reminder.recurring ? (reminder.occurrences_total || 2) : 2);
      setNextTime(toLocalInput(reminder.next_fire_at ? new Date(reminder.next_fire_at) : hoursFromNow(reminder.interval_hours || 1)));
    } else {
      setRecurring(false);
      setIntervalHours(1);
      setOccurrences(2);
      setNextTime(toLocalInput(hoursFromNow(1)));
    }
    setTimeTouched(false);
  }, [open, reminder]);

  // Setting the pattern sets the first fire time (now + interval) — until the
  // user overrides it. Only auto-derive when creating a brand-new reminder.
  const onIntervalChange = (v) => {
    setIntervalHours(v);
    if (!editing && !timeTouched) setNextTime(toLocalInput(hoursFromNow(v)));
  };

  const save = () => {
    const iso = new Date(nextTime).toISOString();
    const total = recurring ? occurrences : 1;
    setSaving(true);
    const req = editing
      ? axios.put(`${REMINDERS_API}/${reminder.id}`, {
          recurring, interval_hours: intervalHours, occurrences_total: total, next_fire_at: iso,
        })
      : axios.post(`${REMINDERS_API}/`, {
          incident_id: incident.id,
          recurring, interval_hours: intervalHours, occurrences_total: total, next_fire_at: iso,
          created_by_name: me?.name || null,
          created_by_email: me?.email || null,
        });
    req.then(() => { onSaved && onSaved(); onClose(); })
       .finally(() => setSaving(false));
  };

  const summary = recurring
    ? `Sends every ${intervalHours} hour${intervalHours === 1 ? "" : "s"}, ${occurrences} times, starting at the time below.`
    : `Sends once, at the time below.`;

  const patient = incident?.patient_name || "the patient";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{editing ? "Edit reminder" : "Set a reminder"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>
            Remind you to review <strong>{patient}</strong>
            {incident?.incident_type ? ` (${incident.incident_type})` : ""}. The reminder is
            sent by SMS to your phone number.
          </DialogContentText>

          <ToggleButtonGroup
            color="primary" exclusive fullWidth size="small"
            value={recurring ? "recurring" : "oneoff"}
            onChange={(e, v) => { if (v) setRecurring(v === "recurring"); }}>
            <ToggleButton value="oneoff">One-off</ToggleButton>
            <ToggleButton value="recurring">Recurring</ToggleButton>
          </ToggleButtonGroup>

          <Stack direction="row" spacing={2}>
            <TextField
              label={recurring ? "Every" : "In"} select fullWidth
              value={intervalHours}
              onChange={(e) => onIntervalChange(Number(e.target.value))}>
              {REMINDER_INTERVAL_HOURS.map((h) => (
                <MenuItem key={h} value={h}>{h} hour{h === 1 ? "" : "s"}</MenuItem>
              ))}
            </TextField>
            {recurring && (
              <TextField
                label="Occurrences" select fullWidth
                value={occurrences}
                onChange={(e) => setOccurrences(Number(e.target.value))}>
                {Array.from({ length: REMINDER_MAX_OCCURRENCES - 1 }, (_, i) => i + 2).map((n) => (
                  <MenuItem key={n} value={n}>{n} times</MenuItem>
                ))}
              </TextField>
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary">{summary}</Typography>

          <TextField
            label="First reminder at" type="datetime-local" fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            value={nextTime}
            onChange={(e) => { setNextTime(e.target.value); setTimeTouched(true); }}
            helperText="Set by the pattern above; adjust if you want the first one at a specific time." />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !nextTime} onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
